import React from 'react';
import { TransactionalPricingResult } from '../../utils/transactionalPricingCalculator';
import { formatTransactionalCurrency, formatTransactionalEmailCount } from '../../utils/transactionalPricingCalculator';

interface TransactionalPriceResultsProps {
  result: TransactionalPricingResult;
  emailCount: number;
}

const TransactionalPriceResults: React.FC<TransactionalPriceResultsProps> = ({ result, emailCount }) => {
  const pricePerThousand = result.slab.pricePerThousand;
  const emailsInThousands = emailCount / 1000;

  return (
    <div className="price-results" role="region" aria-label="Pricing calculation results">
      <h2 className="results-title">Monthly Pricing Breakdown</h2>
      
      <div className="results-grid">
        {/* Email Volume */}
        <div className="result-item">
          <span className="result-label">Email Volume</span>
          <span className="result-value">{formatTransactionalEmailCount(emailCount)} emails/month</span>
        </div>

        {/* Pricing Tier */}
        <div className="result-item">
          <span className="result-label">Pricing Tier</span>
          <span className="result-value">{result.slab.label}</span>
        </div>

        {/* Rate per 1000 */}
        <div className="result-item">
          <span className="result-label">Rate</span>
          <span className="result-value">
            {formatTransactionalCurrency(pricePerThousand, result.currency)} per 1,000 emails
          </span>
        </div>

        {/* Email Cost Calculation */}
        <div className="result-item calculation">
          <span className="result-label">Email Cost</span>
          <div className="calculation-details">
            <span className="calc-formula">
              {emailsInThousands.toFixed(1)} × {formatTransactionalCurrency(pricePerThousand, result.currency)}
            </span>
            <span className="result-value">
              {formatTransactionalCurrency(result.monthlyEmailPrice, result.currency)}
            </span>
          </div>
        </div>

        {/* Discount (if applicable) */}
        {result.discountPercentage > 0 && (
          <>
            <div className="result-item discount">
              <span className="result-label">Discount ({result.discountPercentage}%)</span>
              <span className="result-value discount-amount">
                -{formatTransactionalCurrency(result.discountAmount, result.currency)}
              </span>
            </div>
            <div className="result-item discounted-price">
              <span className="result-label">Discounted Email Cost</span>
              <span className="result-value">
                {formatTransactionalCurrency(result.discountedEmailPrice, result.currency)}
              </span>
            </div>
          </>
        )}

        {/* Dedicated IPs (if applicable) */}
        {result.dedicatedIpCount > 0 && (
          <>
            <div className="result-item">
              <span className="result-label">
                Dedicated IP{result.dedicatedIpCount > 1 ? 's' : ''}
              </span>
              <span className="result-value">
                {result.dedicatedIpCount} × {formatTransactionalCurrency(result.pricePerIp, result.currency)}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Total IP Cost</span>
              <span className="result-value">
                {formatTransactionalCurrency(result.dedicatedIpPrice, result.currency)}/month
              </span>
            </div>
          </>
        )}

        {/* Total Monthly Cost */}
        <div className="result-item total">
          <span className="result-label">Total Monthly Cost</span>
          <span className="result-value total-price">
            {formatTransactionalCurrency(result.totalMonthlyPrice, result.currency)}
          </span>
        </div>
      </div>

      {/* Cost per email info */}
      <div className="cost-per-email">
        <span className="info-text">
          Cost per email: {formatTransactionalCurrency(result.totalMonthlyPrice / emailCount, result.currency)}
          {result.dedicatedIpCount > 0 && (
            <span className="ip-info">
              {' '}(includes {result.dedicatedIpCount} dedicated IP{result.dedicatedIpCount > 1 ? 's' : ''})
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default TransactionalPriceResults;